import { cn } from '@/lib/utils/cn';

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
  /**
   * When true, title and description sit on the same line (description in
   * a smaller muted style to the right of the title) and the bottom
   * margin is reduced. Use on data-dense list pages where every vertical
   * pixel matters (users, campaigns, IVR reports, etc.).
   */
  compact?: boolean;
}

export function PageHeader({
  title,
  description,
  actions,
  className,
  compact,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3',
        compact ? 'mb-2' : 'mb-6 items-start',
        className,
      )}
    >
      <div
        className={cn(
          'min-w-0',
          // When compact, put title + description on the same baseline.
          compact
            ? 'flex flex-wrap items-baseline gap-x-3 gap-y-0.5'
            : undefined,
        )}
      >
        <h1
          className={cn(
            'truncate font-semibold tracking-tight text-foreground',
            compact ? 'text-lg' : 'text-xl',
          )}
        >
          {title}
        </h1>
        {description &&
          (compact ? (
            <span className="truncate text-sm text-muted-foreground">
              {description}
            </span>
          ) : (
            <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
          ))}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
